import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { TariffService } from '@shared-services/tariff.service';
import { NzModalRef } from 'ng-zorro-antd/modal';
import { Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Component({
  selector: 'app-table-code-label',
  templateUrl: './table-code-label.component.html',
  styleUrl: './table-code-label.component.less'
})
export class TableCodeLabelComponent implements OnInit {
  controlType: string = '';
  refTypeCode: string = '';
  selectedValues: any[] = [];

  loading = false;
  filteredData: any[] = [];
  pageIndex = 0;
  pageSize = 10;
  total = 0;
  isInvalid = false;

  // Max codes sent per RDM request - keeps the "codes" query param well under the
  // HTTP/2 header size limit (sending all selected codes in one request was causing
  // ERR_HTTP2_PROTOCOL_ERROR once the list ran into the thousands).
  private readonly CODES_BATCH_SIZE = 100;
  // Full, unpaginated result set for the RDM path - fetched once in batches, then
  // paginated on the client so page changes don't need another network call.
  private allRdmResults: any[] = [];

  constructor(
    private modalRef: NzModalRef,
    private http: HttpClient,
    private tariffService: TariffService,
    private cdr: ChangeDetectorRef,
    private router: Router
  ) { }

  ngOnInit(): void {
    const modalData = this.modalRef.getConfig().nzData;
    this.controlType = modalData.controlType;
    this.refTypeCode = modalData.refTypeCode;
    this.selectedValues = modalData.selectedValues || [];
    this.loading = true;
    if (modalData.isInvalid) {
      this.isInvalid = true;
      this.filteredData = modalData.selectedValues;
      this.loading = false;
      return;
    }
    this.loadSelectedData();
  }

  private loadSelectedData(): void {
    if (!this.selectedValues.length) return;
    const codes = this.selectedValues.map((v: any) => v.code);
    if (this.controlType === 'hscode') {
      this.findHsCode(codes);
    } else {
      this.loadRdmData(codes);
    }
  }

  // Fetches every selected code's label in small batches (instead of one request with
  // all codes in the query string), merges the results, then paginates client-side.
  private loadRdmData(codes: string[]): void {
    const batches: string[][] = [];
    for (let i = 0; i < codes.length; i += this.CODES_BATCH_SIZE) {
      batches.push(codes.slice(i, i + this.CODES_BATCH_SIZE));
    }

    const requests = batches.map(batch => {
      const url = `${environment.baseUrl}/rdm/api/v1/values/search/filter?page=0&size=${batch.length}&refTypeLabel=${this.refTypeCode}&codes=${batch.join(',')}`;
      return this.http.get<any>(url).pipe(catchError(() => of({ content: [] })));
    });

    forkJoin(requests).subscribe({
      next: (responses: any[]) => {
        this.allRdmResults = responses.reduce((acc: any[], response: any) => acc.concat(response.content || []), []);
        this.total = this.allRdmResults.length;
        this.applyClientPagination();
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.allRdmResults = [];
        this.filteredData = [];
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }

  private applyClientPagination(): void {
    const start = this.pageIndex * this.pageSize;
    this.filteredData = this.allRdmResults.slice(start, start + this.pageSize);
  }

  private findHsCode(codes: string[]): void {
    this.tariffService.getCommodityHSCode(codes, '', '', this.pageSize, this.pageIndex).subscribe({
      next: (response: any) => {
        if (this.router.url.includes('/view-valuation')) {
          this.filteredData = (response.tariffSearchDto.content || [])
            .map((item: any) => ({
              code: item.tariffCode,
              uom: item.uom,
              label: item.descriptionInEnglish || item.description
            }));
        } else {
          this.filteredData = (response.tariffSearchDto.content || [])
            .map((item: any) => ({
              code: item.tariffCode,
              uom: item.uom,
              label: item.description || item.descriptionInEnglish
            }));
        }

        this.total = response.tariffSearchDto.totalElements || 0;
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.filteredData = [];
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }

  onPageChange(page: number): void {
    this.pageIndex = page - 1;
    if (this.controlType === 'hscode') {
      this.loading = true;
      this.findHsCode(this.selectedValues.map((v: any) => v.code));
    } else {
      // RDM data is already cached locally from the initial batched load - just re-slice it.
      this.applyClientPagination();
    }
  }
}